import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Receipt, Loader2, Download, RotateCw, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';

const STATUS_CFG = {
  paid: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  sent: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',
  cancelled: 'bg-slate-500/15 text-slate-400 border border-slate-500/30',
};

const PAGE_SIZE = 10;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const inr = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

export default function InvoicesAdminTab() {
  const [data, setData] = useState({ invoices: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (clientName.trim()) params.client_name = clientName.trim();
      if (status) params.status = status;
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate + 'T23:59:59').toISOString();
      const res = await adminApi.listInvoices(params);
      setData(res || { invoices: [], totals: {} });
      setPage(1); // reset to first page whenever filters change
    } catch (e) {
      toast.error('Failed to load invoices');
    } finally { setLoading(false); }
  }, [clientName, status, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const allInvoices = data.invoices || [];
  const totalPages = Math.max(1, Math.ceil(allInvoices.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedInvoices = useMemo(
    () => allInvoices.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [allInvoices, currentPage]
  );

  const downloadPdf = async (inv) => {
    setDownloading(inv.id);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Invoice-${inv.invoice_number.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Failed to download'); }
    finally { setDownloading(null); }
  };

  const totals = data.totals || {};

  return (
    <div className="space-y-4" data-testid="admin-invoices-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-white">Invoices</h2>
          <span className="text-xs text-slate-400">({totals.count || 0})</span>
        </div>
        <button onClick={load} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="invoices-refresh">
          <RotateCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 flex items-end gap-3 flex-wrap" data-testid="invoices-filters">
        <div className="flex items-center text-xs text-slate-400 gap-1"><Filter className="w-3.5 h-3.5" />Filters</div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-1">Client name</label>
          <input value={clientName} onChange={(e) => setClientName(e.target.value)}
            data-testid="filter-client-name"
            placeholder="Search…"
            className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded text-white focus:outline-none focus:ring-1 focus:ring-emerald-500" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-1">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status"
            className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded text-white">
            <option value="">All</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-1">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="filter-start-date"
            className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded text-white" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-1">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="filter-end-date"
            className="h-8 px-2 text-sm bg-slate-800 border border-slate-700 rounded text-white" />
        </div>
        {(clientName || status || startDate || endDate) && (
          <button onClick={() => { setClientName(''); setStatus(''); setStartDate(''); setEndDate(''); }}
            data-testid="filter-clear"
            className="h-8 px-3 text-xs text-slate-300 hover:text-white border border-slate-600 rounded ml-auto">Clear</button>
        )}
      </div>

      {/* Totals bar */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs" data-testid="invoices-totals">
        {[
          { label: 'Sub Total', val: totals.sub_total, key: 'sub' },
          { label: 'CGST', val: totals.cgst_total, key: 'cgst' },
          { label: 'SGST', val: totals.sgst_total, key: 'sgst' },
          { label: 'IGST', val: totals.igst_total, key: 'igst' },
          { label: 'Total GST', val: (totals.cgst_total || 0) + (totals.sgst_total || 0) + (totals.igst_total || 0), key: 'tgst' },
          { label: 'Grand Total', val: totals.grand_total, key: 'grand', highlight: true },
        ].map((s) => (
          <div key={s.key} className={`p-3 rounded-lg border ${s.highlight ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/50 border-slate-700'}`} data-testid={`totals-${s.key}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{s.label}</div>
            <div className={`text-sm font-semibold mt-0.5 ${s.highlight ? 'text-emerald-400' : 'text-white'}`}>{inr(s.val)}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-14 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
      ) : allInvoices.length === 0 ? (
        <div className="text-center py-14 rounded-xl bg-slate-800/40 border border-slate-700">
          <Receipt className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-400 text-sm">No invoices match the current filters.</p>
        </div>
      ) : (
        <>
        <div className="overflow-x-auto rounded-xl border border-slate-700 bg-slate-800/40">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-slate-900/40">
              <tr>
                <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Invoice #</th>
                <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Client</th>
                <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Date</th>
                <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Subtotal</th>
                <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">CGST</th>
                <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">SGST</th>
                <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">IGST</th>
                <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Total</th>
                <th className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Status</th>
                <th className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedInvoices.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-700 hover:bg-slate-800/60" data-testid={`admin-invoice-row-${inv.id}`}>
                  <td className="px-3 py-2.5 font-mono text-xs text-emerald-400">{inv.invoice_number}</td>
                  <td className="px-3 py-2.5 text-white">{inv.client_name || '—'}<div className="text-[11px] text-slate-500">{inv.client_state}</div></td>
                  <td className="px-3 py-2.5 text-slate-300">{fmtDate(inv.invoice_date)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-200">{inr(inv.sub_total)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-300">{inv.cgst_total ? inr(inv.cgst_total) : <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-2.5 text-right text-slate-300">{inv.sgst_total ? inr(inv.sgst_total) : <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-2.5 text-right text-slate-300">{inv.igst_total ? inr(inv.igst_total) : <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-white">{inr(inv.total_amount)}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide ${STATUS_CFG[inv.status] || STATUS_CFG.pending}`}>{inv.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => downloadPdf(inv)} disabled={downloading === inv.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50"
                      data-testid={`admin-invoice-download-${inv.id}`}>
                      {downloading === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="flex items-center justify-between mt-3 px-2" data-testid="invoices-pagination">
          <div className="text-xs text-slate-400">
            Showing <span className="text-white font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>
            {' – '}
            <span className="text-white font-medium">{Math.min(currentPage * PAGE_SIZE, allInvoices.length)}</span>
            {' of '}
            <span className="text-white font-medium">{allInvoices.length}</span> invoices
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="invoices-page-prev"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </button>
            <span className="text-xs text-slate-300 px-1">
              Page <span className="text-white font-medium">{currentPage}</span> / <span className="text-white font-medium">{totalPages}</span>
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="invoices-page-next"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
